import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TabBar } from '../TabBar';
import { MobileHeader } from '../MobileHeader';
import { AlertCard } from '../AlertCard';
import MetricCard from "../MetricCard";
import { UsageChart } from "../UsageChart";
import { AreaComparisonChart, YourBadges, ViewAllTipsButton } from "../AreaComparison";
import { QuickAccessCard } from "../QuickAccessCard";
import UsagePieChart from "../UsagePieChart";
import ToastNotification from "../ToastNotification";

type TimeFrameType = 'daily' | 'weekly' | 'monthly';

const Dashboard: React.FC = () => {
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>('daily');
  
  // Helper function to render content based on timeFrame without TS errors
  const renderTimeFrameContent = () => {
    switch(timeFrame) {
      case 'daily':
        return 'Daily Usage';
      case 'weekly':
        return 'Weekly Usage';
      case 'monthly':
        return 'Monthly Usage';
      default:
        return 'Usage';
    }
  };
  
  // Helper function to generate button styles
  const getButtonStyle = (buttonTimeFrame: TimeFrameType) => {
    return timeFrame === buttonTimeFrame ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600';
  };
  
  // Helper function to render the chart content based on timeFrame
  const renderChartContent = () => {
    switch(timeFrame) {
      case 'daily':
        return (
          <div className="h-60">
            <h3 className="mb-2 font-medium text-gray-700">Usage Breakdown</h3>
            <div className="bg-white h-full flex flex-col">
              {/* Y-axis labels */}
              <div className="flex justify-between mb-1 text-sm text-gray-500">
                <div>40L</div>
              </div>
              
              <div className="h-px bg-gray-200 w-full mb-4"></div>
              
              {/* Bar chart with exact heights as in Image 2 */}
              <div className="flex items-end justify-between h-48 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                  // Values in liters matching Image 2
                  const values = [32, 38, 30, 34, 28, 24, 32];
                  // Calculate height percentage based on maximum value (40L)
                  const heightPercentage = (values[index] / 40) * 100;
                  
                  return (
                    <div key={day} className="flex flex-col items-center flex-1">
                      <div 
                        className="w-12 bg-blue-500 rounded-t-md" 
                        style={{ height: `${heightPercentage}%` }}
                      ></div>
                    </div>
                  );
                })}
              </div>
              
              <div className="h-px bg-gray-200 w-full mb-1"></div>
              <div className="text-sm text-gray-500 ml-0">0L</div>
              
              <div className="grid grid-cols-7 gap-1 text-center mt-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-sm text-gray-600">{day}</div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'weekly':
        return (
          <div>
            <h3 className="mb-4 font-medium text-gray-700">Usage Breakdown</h3>
            <div className="h-64 flex justify-center items-center">
              <UsagePieChart 
                data={usageData.weekly.usageBreakdown}
                showCard={false}
                containerClass="h-full w-full"
              />
            </div>
          </div>
        );
      case 'monthly':
        return (
          <div>
            <div className="h-48">
              <UsagePieChart 
                data={usageData.monthly.usageBreakdown}
                showCard={false}
                containerClass="h-full w-full"
                title="Monthly Usage Breakdown"
              />
            </div>
          </div>
        );
      default:
        return null;
    }
  };
  const [waterUsageToggle, setWaterUsageToggle] = useState<boolean>(true);
  
  // Function to toggle water usage data
  const toggleWaterUsage = () => {
    setWaterUsageToggle(!waterUsageToggle);
    // Add toast notification when toggling data
    setToast({
      type: 'info',
      message: `Switched to ${waterUsageToggle ? 'personal' : 'household'} usage data`,
      description: `Now showing ${waterUsageToggle ? 'personal' : 'household'} water consumption metrics`
    });
  };
  const [toast, setToast] = useState<{type: 'success' | 'info' | 'warning' | 'error', message: string, description?: string} | null>(null);
  
  // Show a random toast notification after component mounts
  useEffect(() => {
    const toasts = [
      { type: 'success' as const, message: 'You saved 5L this week!', description: 'Great job conserving water' },
      { type: 'info' as const, message: 'Peak usage at 7 AM', description: 'Consider shifting your morning routine' },
      { type: 'success' as const, message: 'Weekly goal achieved!', description: 'You used 15% less water than your target' }
    ];
    
    const randomToast = toasts[Math.floor(Math.random() * toasts.length)];
    
    const timer = setTimeout(() => {
      setToast(randomToast);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Sample alerts data with actions
  const alerts = [
    {
      id: "alert-1",
      title: "Stage 1 Restrictions Active",
      description: "Limited outdoor water usage",
      type: "warning",
      action: () => {
        // Navigate to water restrictions page
        window.location.href = "/restrictions";
      }
    },
    {
      id: "alert-2",
      title: "High Usage Alert",
      description: "Potential Leak",
      type: "warning",
      action: () => {
        // Navigate to leak detection page
        window.location.href = "/leak-detection";
      }
    }
  ];

  // Generate mock data for each time frame
  const usageData = {
    daily: waterUsageToggle ? {
      value: '125L',
      changePercentage: '10%',
      changeDirection: 'up' as 'up',
      comparisonText: 'vs yesterday',
      usageBreakdown: [
        { category: 'Shower', percentage: 35, color: '#4F46E5', liters: 44 },
        { category: 'Kitchen', percentage: 25, color: '#60A5FA', liters: 31 },
        { category: 'Laundry', percentage: 20, color: '#2563EB', liters: 25 },
        { category: 'Toilet', percentage: 15, color: '#93C5FD', liters: 19 },
        { category: 'Other', percentage: 5, color: '#6B7280', liters: 6 }
      ]
    } : {
      value: '105L',
      changePercentage: '16%',
      changeDirection: 'down' as 'down',
      comparisonText: 'vs yesterday',
      usageBreakdown: [
        { category: 'Shower', percentage: 30, color: '#4F46E5', liters: 32 },
        { category: 'Kitchen', percentage: 28, color: '#60A5FA', liters: 29 },
        { category: 'Laundry', percentage: 22, color: '#2563EB', liters: 23 },
        { category: 'Toilet', percentage: 12, color: '#93C5FD', liters: 13 },
        { category: 'Other', percentage: 8, color: '#6B7280', liters: 8 }
      ]
    },
    weekly: waterUsageToggle ? {
      value: '930L',
      changePercentage: '5%',
      changeDirection: 'down' as 'down',
      comparisonText: 'vs last week',
      usageBreakdown: [
        { category: 'Shower', percentage: 35, color: '#4F46E5', liters: 326 },
        { category: 'Kitchen', percentage: 25, color: '#60A5FA', liters: 233 },
        { category: 'Laundry', percentage: 20, color: '#2563EB', liters: 186 },
        { category: 'Toilet', percentage: 15, color: '#93C5FD', liters: 140 },
        { category: 'Other', percentage: 5, color: '#6B7280', liters: 45 }
      ]
    } : {
      value: '875L',
      changePercentage: '12%',
      changeDirection: 'down' as 'down',
      comparisonText: 'vs last week',
      usageBreakdown: [
        { category: 'Shower', percentage: 32, color: '#4F46E5', liters: 280 },
        { category: 'Kitchen', percentage: 26, color: '#60A5FA', liters: 228 },
        { category: 'Laundry', percentage: 22, color: '#2563EB', liters: 193 },
        { category: 'Toilet', percentage: 13, color: '#93C5FD', liters: 114 },
        { category: 'Other', percentage: 7, color: '#6B7280', liters: 60 }
      ]
    },
    monthly: waterUsageToggle ? {
      value: '3,850L',
      changePercentage: '12%',
      changeDirection: 'down' as 'down',
      comparisonText: 'vs last month',
      usageBreakdown: [
        { category: 'Shower', percentage: 30, color: '#4F46E5', liters: 1155 },
        { category: 'Kitchen', percentage: 25, color: '#60A5FA', liters: 963 },
        { category: 'Laundry', percentage: 25, color: '#2563EB', liters: 963 },
        { category: 'Toilet', percentage: 15, color: '#93C5FD', liters: 578 },
        { category: 'Other', percentage: 5, color: '#6B7280', liters: 192 }
      ]
    } : {
      value: '3,450L',
      changePercentage: '20%',
      changeDirection: 'down' as 'down',
      comparisonText: 'vs last month',
      usageBreakdown: [
        { category: 'Shower', percentage: 28, color: '#4F46E5', liters: 966 },
        { category: 'Kitchen', percentage: 27, color: '#60A5FA', liters: 932 },
        { category: 'Laundry', percentage: 23, color: '#2563EB', liters: 794 },
        { category: 'Toilet', percentage: 14, color: '#93C5FD', liters: 483 },
        { category: 'Other', percentage: 8, color: '#6B7280', liters: 276 }
      ]
    }
  };
  
  // Icons for the dashboard
  const icons = {
    restrictionIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/eb698c5a88a5632ef50fd26fddad0ca6a6f9d66d",
    leakIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/4d3a1a4dd428ce924593360021a75c4b7f2606fa",
    closeIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/a44198589ca52d78e53ed7751b4de8acb0c71314",
    customizeIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/98873c42c363b0747f8730f469254b33c467a408",
    exportIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/96826cba18af6dbfabe114c02d88b628f462297d",
    chartIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/56b7f4b5885834e93d54aa97d497bc9d25e76907",
    dropdownIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/174fe0ec676bf4780e267cf5e23f22bfc0464194",
    arrowDownIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/54b911beeef099a585d0fbe2bebf6499ab7eae8e",
    detailedUsageIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/522bdd018c277298f01ff97bbf9bfd1d93d5bc0c",
    tipsIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/756ac855c22c250d8660dbd1664e65239c92fc9e",
    leakReportIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/c00924532506281a23e1c6759cda7c11e4ce1a73",
    settingsIcon:
      "https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/ff5b2244331e5cb3fe96bb35a9fa0a7e51992109",
  };

  return (
    <main className="mx-auto w-full max-w-lg bg-slate-50 min-h-screen pb-[70px]">
      <TabBar />
      <section className="overflow-x-hidden w-full bg-white">
        <MobileHeader />
        <section className="pt-6 w-full">
          <header className="w-full font-semibold px-4">
            <div className="w-full">
              <h1 className="w-full text-2xl text-slate-800 m-0">My usage</h1>
              
              {/* Time period toggle */}
              <div className="mt-3 bg-gray-100 p-1 rounded-lg flex w-full max-w-xs">
                <button 
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${timeFrame === 'daily' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setTimeFrame('daily')}
                >
                  Daily
                </button>
                <button 
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${timeFrame === 'weekly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setTimeFrame('weekly')}
                >
                  Weekly
                </button>
                <button 
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${timeFrame === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setTimeFrame('monthly')}
                >
                  Monthly
                </button>
              </div>
              
              {/* Alerts section - Moved to top by toggle */}
              <div className="flex flex-col space-y-3 mt-5 mb-5">
                {alerts.map(alert => (
                  <div key={alert.id} 
                    className="p-4 bg-red-50 rounded-lg border border-red-100 flex items-start space-x-3 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => alert.action()}
                  >
                    <div className="p-1.5 rounded-full bg-red-100 flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-red-800">{alert.title}</h3>
                      <p className="text-sm text-red-700 mt-1">{alert.description}</p>
                    </div>
                    <button 
                      className="text-gray-400 hover:text-gray-500"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent's onClick
                        // Remove this alert
                        const newAlerts = alerts.filter(a => a.id !== alert.id);
                        // You would typically update this in state or context
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3 items-center mt-4">
                <button 
                  onClick={() => setToast({ type: 'info', message: 'Settings updated', description: 'Dashboard customization options saved' })}
                  className="flex gap-1 justify-center items-center py-2.5 px-3.5 bg-white rounded-lg border border-solid border-gray-300 shadow-sm text-sm font-medium text-slate-600 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img src={icons.customizeIcon} alt="" className="w-5 h-5" />
                  <span>Customize</span>
                </button>
                <button 
                  onClick={() => setToast({ type: 'success', message: 'Export complete', description: 'Usage data has been exported' })}
                  className="flex gap-1 justify-center items-center py-2.5 px-3.5 bg-white rounded-lg border border-solid border-gray-300 shadow-sm text-sm font-medium text-slate-600 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img src={icons.exportIcon} alt="" className="w-5 h-5" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </header>

          <div className="px-4 pb-12 w-full">
            {/* Toast Notification */}
            {toast && (
              <ToastNotification
                type={toast.type}
                message={toast.message}
                description={toast.description}
                onClose={() => setToast(null)}
              />
            )}
            


            <MetricCard
              title={timeFrame === 'daily' ? 'Today\'s Usage' : timeFrame === 'weekly' ? 'Weekly Usage' : 'This Month\'s Usage'}
              value={usageData[timeFrame].value}
              changePercentage={usageData[timeFrame].changePercentage}
              changeDirection={usageData[timeFrame].changeDirection}
              comparisonText={usageData[timeFrame].comparisonText}
              chartImageUrl={icons.chartIcon}
              className="mt-6"
            />

            {timeFrame === 'weekly' ? (
              <>
                <div className="mt-6 bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {renderTimeFrameContent()}
                    </h3>
                    <div className="flex space-x-2">
                      <button 
                        className={`py-1.5 px-4 rounded-full text-sm ${getButtonStyle('daily')}`}
                        onClick={() => setTimeFrame('daily')}
                      >
                        Day
                      </button>
                      <button 
                        className={`py-1.5 px-4 rounded-full text-sm ${getButtonStyle('weekly')}`}
                        onClick={() => setTimeFrame('weekly')}
                      >
                        Week
                      </button>
                      <button 
                        className={`py-1.5 px-4 rounded-full text-sm ${getButtonStyle('monthly')}`}
                        onClick={() => setTimeFrame('monthly')}
                      >
                        Month
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-4 mb-2">
                    <div className="text-sm text-gray-600">Toggle data source</div>
                    <button 
                      onClick={toggleWaterUsage}
                      className="flex items-center justify-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      {waterUsageToggle ? 'Household' : 'Personal'} Usage
                      <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m-4 6H4m0 0l4 4m-4-4l4-4"></path>
                      </svg>
                    </button>
                  </div>
                  
                  <div className="mt-4">
                    {renderChartContent()}
                  </div>
                </div>

                {/* We've moved the pie chart inside the weekly usage card */}
              </>
            ) : (
              <UsageChart className="mt-6" />
            )}

            <div className="flex gap-4 w-full mt-4">
              <Link to="/usage" className="w-full">
                <QuickAccessCard
                  icon={icons.detailedUsageIcon}
                  title="Detailed Usage"
                  dropdownIcon={icons.dropdownIcon}
                />
              </Link>
              <Link to="/conservation-tips" className="w-full">
                <QuickAccessCard
                  icon={icons.tipsIcon}
                  title="Conservation Tips"
                  dropdownIcon={icons.arrowDownIcon}
                />
              </Link>
            </div>

            <div className="flex gap-4 w-full mt-4">
              <Link to="/leak-detection" className="w-full">
                <QuickAccessCard
                  icon={icons.leakReportIcon}
                  title="Report Leak"
                  dropdownIcon={icons.dropdownIcon}
                />
              </Link>
              <Link to="/settings" className="w-full">
                <QuickAccessCard
                  icon={icons.settingsIcon}
                  title="Settings"
                  dropdownIcon={icons.arrowDownIcon}
                />
              </Link>
            </div>

            <div className="flex gap-4 w-full mt-4">
              <Link to="/alerts" className="w-full">
                <QuickAccessCard
                  icon={icons.leakReportIcon}
                  title="Alerts Centre"
                  dropdownIcon={icons.dropdownIcon}
                />
              </Link>
              <Link to="/conservation" className="w-full">
                <QuickAccessCard
                  icon={icons.settingsIcon}
                  title="Conservation Hub"
                  dropdownIcon={icons.arrowDownIcon}
                />
              </Link>
            </div>

            {/* Area Comparison with proper spacing */}
            <div className="mt-10 mb-10">
              <AreaComparisonChart />
            </div>
            
            {/* Badges Section - with title here but not in the component */}
            <div className="mt-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">Your Badges</h3>
              <YourBadges showTitle={false} />
            </div>
            
            {/* Call to action button */}
            <div className="mt-8 mb-16">
              <ViewAllTipsButton />
            </div>
          </div>
        </section>
      </section>
    </main>
  );
};

export default Dashboard;